import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "../context/AuthContext";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Single login entry point — route by role *after* React has applied
  // the new `user` state. Using useEffect avoids the race condition where
  // AdminGuard would see the stale (null) user during the navigation.
  useEffect(() => {
    if (user) {
      navigate(user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      // navigation happens in the useEffect above
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4" style={{backgroundImage: "radial-gradient(ellipse at 50% 50%, rgba(0,229,255,0.03) 0%, transparent 70%)"}}>
      {/* Grid background */}
      <div className="absolute inset-0 opacity-5" style={{backgroundImage: "linear-gradient(rgba(0,229,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(0,229,255,0.3) 1px, transparent 1px)", backgroundSize: "60px 60px"}} />

      <div className="relative z-10 w-full max-w-md">
        {/* Back to homepage */}
        <button
          data-testid="back-to-homepage"
          onClick={() => navigate("/")}
          className="mb-6 inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#A0AEC0] hover:text-[#00E5FF] transition-colors"
        >
          <ArrowLeft className="w-3 h-3" /> Torna alla homepage
        </button>

        {/* Logo */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center">
            <img
              src="/branding/moonstone-analytics.png"
              alt="Moonstone Analytics"
              style={{ height: 96, width: "auto", display: "block" }}
              draggable={false}
            />
          </div>
        </div>

        {/* Card */}
        <div className="bg-[#0D0F12] border border-white/[0.04] p-8 rounded-2xl">
          <p className="text-xs text-[#A0AEC0] tracking-[0.2em] uppercase mb-6">Authentication Required</p>

          {error && (
            <div data-testid="login-error" className="mb-4 p-3 border border-[#FF3B30]/30 bg-[#FF3B30]/5 text-[#FF3B30] text-sm rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs text-[#CBD5E1] tracking-[0.15em] uppercase mb-2">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full bg-[#050505] border border-white/[0.04] text-white px-3 py-2.5 text-sm focus:outline-none focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF]/30 transition-colors"
                placeholder="user@company.com"
                required
              />
            </div>

            <div>
              <label className="block text-xs text-[#CBD5E1] tracking-[0.15em] uppercase mb-2">Password</label>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full bg-[#050505] border border-white/[0.04] text-white px-3 py-2.5 pr-10 text-sm focus:outline-none focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF]/30 transition-colors"
                  placeholder="••••••••"
                  required
                />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0AEC0] hover:text-[#CBD5E1]">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#00E5FF] text-black font-semibold py-2.5 text-sm tracking-wide hover:bg-[#00B3CC] transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-2"
            >
              {loading ? "AUTHENTICATING..." : "ACCESS PLATFORM"}
            </button>
          </form>

          <div className="mt-6 pt-4">
            <p className="text-xs text-[#A0AEC0] text-center">Demo credentials: demo@acme.com / Demo@2024!</p>
          </div>
        </div>

          <p className="text-center text-xs text-[#A0AEC0] mt-4">
          Moonstone Analytics V2.89 | Intelligence for Business
        </p>
      </div>
    </div>
  );
}
